import { useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FieldError } from '@/components/ui/field'
import { listSentence } from '@/lib/formatters'
import { blockingChecks, type FillApplicationState, hasFilesReading } from '@/models/fillApplication'
import { EmailRecipientsLine } from './EmailRecipientsLine'
import { FILL_STEP_LABELS, FILL_STEPS, stepForCard } from './fillSteps'
import { MissingItemsList } from './MissingItemsList'

interface SendToCarriersCardProps {
  state: FillApplicationState
  /** The tabs with unsaved edits, which Send saves first. */
  dirtyLabels: string[]
  sending: boolean
  error: string | null
  /** Bumped when the server refused for missing items; focus moves to the list. */
  blockedNonce: number
  onSend: () => void
}

export function SendToCarriersCard({
  state,
  dirtyLabels,
  sending,
  error,
  blockedNonce,
  onSend,
}: SendToCarriersCardProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const blocking = blockingChecks(state.checks)
  const groups = FILL_STEPS.map((step) => ({
    step,
    items: blocking.filter((check) => stepForCard(check.card) === step),
  })).filter((group) => group.items.length > 0)
  const reading = hasFilesReading(state)

  useEffect(() => {
    if (blockedNonce > 0) listRef.current?.focus()
  }, [blockedNonce])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send to carriers</CardTitle>
        <CardDescription>Cai asks every carrier that fits for a price, as the customer.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {blocking.length > 0 ? (
          <div ref={listRef} tabIndex={-1} className="flex flex-col gap-3 outline-none">
            <p className="text-error text-sm font-semibold">
              {blocking.length} {blocking.length === 1 ? 'thing' : 'things'} to fix before sending
            </p>
            {groups.map(({ step, items }) => (
              <div key={step} className="flex flex-col gap-1">
                <span className="text-ink-500 text-xs font-semibold tracking-wide uppercase">
                  {FILL_STEP_LABELS[step]}
                </span>
                <MissingItemsList items={items} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm">Everything the carriers need is filled in.</p>
        )}
        <EmailRecipientsLine recipients={state.emailRecipients} priceEmails={state.priceEmails} />
        <FieldError className="text-xs">{error}</FieldError>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            className="text-md"
            onClick={onSend}
            disabled={reading}
            loading={sending ? 'Sending…' : undefined}
          >
            {dirtyLabels.length > 0 ? 'Save and send to carriers' : 'Send to carriers'}
          </Button>
          {dirtyLabels.length > 0 && (
            <span className="text-ink-500 text-sm">Saves {listSentence(dirtyLabels)} first.</span>
          )}
          {reading && <span className="text-ink-500 text-sm">Wait until every file is read.</span>}
        </div>
      </CardContent>
    </Card>
  )
}
